/**
 * The payload builders. A QR code only ever holds text; what makes a code
 * "a Wi-Fi code" or "a contact card" is a convention about how that text is
 * written, and every scanner on a phone knows the handful below.
 *
 * All pure, all tested — this is the part that quietly breaks if a comma is
 * left unescaped, and the symptom is a code that scans into gibberish.
 */

export type Kind = "link" | "text" | "wifi" | "email" | "sms" | "phone" | "vcard";

export interface Fields {
  link: { url: string };
  text: { text: string };
  wifi: { ssid: string; password: string; security: "WPA" | "WEP" | "nopass"; hidden: boolean };
  email: { to: string; subject: string; body: string };
  sms: { number: string; message: string };
  phone: { number: string };
  vcard: {
    first: string;
    last: string;
    org: string;
    title: string;
    phone: string;
    email: string;
    url: string;
  };
}

export const EMPTY: Fields = {
  link: { url: "" },
  text: { text: "" },
  wifi: { ssid: "", password: "", security: "WPA", hidden: false },
  email: { to: "", subject: "", body: "" },
  sms: { number: "", message: "" },
  phone: { number: "" },
  vcard: { first: "", last: "", org: "", title: "", phone: "", email: "", url: "" },
};

export const KINDS: { value: Kind; label: string }[] = [
  { value: "link", label: "Link" },
  { value: "text", label: "Text" },
  { value: "wifi", label: "Wi-Fi" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "phone", label: "Phone" },
  { value: "vcard", label: "Contact" },
];

/** Turns the form for one kind into the string that goes into the code. */
export function buildPayload<K extends Kind>(kind: K, fields: Fields[K]): string {
  switch (kind) {
    case "link":
      return normaliseUrl((fields as Fields["link"]).url);
    case "text":
      return (fields as Fields["text"]).text;
    case "wifi": {
      const f = fields as Fields["wifi"];
      if (!f.ssid.trim()) return "";
      // WIFI:T:WPA;S:name;P:secret;H:true;;  — the trailing ";;" is part of it.
      const parts = [`T:${f.security}`, `S:${escapeWifi(f.ssid)}`];
      if (f.security !== "nopass") parts.push(`P:${escapeWifi(f.password)}`);
      if (f.hidden) parts.push("H:true");
      return `WIFI:${parts.join(";")};;`;
    }
    case "email": {
      const f = fields as Fields["email"];
      if (!f.to.trim()) return "";
      const query = [
        f.subject.trim() ? `subject=${encodeURIComponent(f.subject)}` : "",
        f.body.trim() ? `body=${encodeURIComponent(f.body)}` : "",
      ].filter(Boolean);
      return `mailto:${f.to.trim()}${query.length > 0 ? `?${query.join("&")}` : ""}`;
    }
    case "sms": {
      const f = fields as Fields["sms"];
      if (!f.number.trim()) return "";
      const number = tidyNumber(f.number);
      // SMSTO: is the form Android and iOS both prefill the message from.
      return f.message.trim() ? `SMSTO:${number}:${f.message}` : `SMSTO:${number}`;
    }
    case "phone": {
      const f = fields as Fields["phone"];
      return f.number.trim() ? `tel:${tidyNumber(f.number)}` : "";
    }
    case "vcard": {
      const f = fields as Fields["vcard"];
      const name = `${f.first} ${f.last}`.trim();
      if (!name && !f.org.trim() && !f.phone.trim() && !f.email.trim()) return "";
      const lines = [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `N:${escapeVcard(f.last)};${escapeVcard(f.first)};;;`,
        `FN:${escapeVcard(name || f.org)}`,
      ];
      if (f.org.trim()) lines.push(`ORG:${escapeVcard(f.org)}`);
      if (f.title.trim()) lines.push(`TITLE:${escapeVcard(f.title)}`);
      if (f.phone.trim()) lines.push(`TEL;TYPE=CELL:${tidyNumber(f.phone)}`);
      if (f.email.trim()) lines.push(`EMAIL:${escapeVcard(f.email.trim())}`);
      if (f.url.trim()) lines.push(`URL:${escapeVcard(normaliseUrl(f.url))}`);
      lines.push("END:VCARD");
      // CRLF: the vCard spec's line break, and the one Android's parser wants.
      return lines.join("\r\n");
    }
    default:
      return "";
  }
}

/**
 * Types-in "ottercraft.dev", means "https://ottercraft.dev". Anything that
 * already names a scheme (including mailto: or upi:) is left exactly as typed.
 */
export function normaliseUrl(raw: string): string {
  const url = raw.trim();
  if (url === "") return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  return `https://${url}`;
}

/** Backslash, semicolon, comma, colon and quote are what a Wi-Fi payload escapes. */
function escapeWifi(value: string): string {
  return value.replace(/([\\;,:"])/g, "\\$1");
}

/** vCard escapes backslash, comma, semicolon, and turns newlines into \n. */
function escapeVcard(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;")
    .replace(/\r?\n/g, "\\n")
    .trim();
}

/** Keeps the digits, a leading +, and nothing else a dialler would choke on. */
function tidyNumber(value: string): string {
  const trimmed = value.trim();
  const plus = trimmed.startsWith("+") ? "+" : "";
  return plus + trimmed.replace(/[^\d]/g, "");
}

/**
 * A short, recognisable file name for the download: the host for a link, the
 * network for Wi-Fi, the person for a contact, the first few words otherwise.
 */
export function suggestFilename(kind: Kind, fields: Fields[Kind], payload: string): string {
  const slug = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^\w\s.-]/g, "")
      .trim()
      // A dot is a word break in a host name, not a file extension here.
      .replace(/[\s._-]+/g, "-")
      .slice(0, 40)
      .replace(/^-|-$/g, "");

  let base = "";
  if (kind === "link") {
    try {
      base = new URL(payload).hostname.replace(/^www\./, "");
    } catch {
      base = "";
    }
  } else if (kind === "wifi") {
    base = (fields as Fields["wifi"]).ssid;
  } else if (kind === "vcard") {
    const f = fields as Fields["vcard"];
    base = `${f.first} ${f.last}`.trim() || f.org;
  } else if (kind === "phone" || kind === "sms") {
    base = (fields as Fields["phone"]).number;
  } else {
    base = (fields as Fields["text"]).text.split(/\s+/).slice(0, 4).join(" ");
  }
  return `qr-${slug(base) || "code"}`;
}
